/**
 * Ava & Bear mix-and-match nursery bundle (sections/ab-bundle.liquid).
 *
 * Each slot (pod, nursing cushion, changing mat set) has its own print <select>. Option data is
 * rendered by Liquid as JSON. Changing a print updates the slot's photo, swatch, price and
 * fulfilment label and recalculates the separate total, saving and bundle price.
 * "Add bundle to bag" posts all three variants in one /cart/add.js request, each tagged with the
 * same `_bundle` id and `_bundle_name`, then announces the change with the theme's
 * CartLinesUpdateEvent so the cart drawer, cart icon and cart items refresh like a normal add.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} BundleOption
 * @property {number} productId
 * @property {number} variantId
 * @property {string} title
 * @property {string} print
 * @property {string} url
 * @property {number} price
 * @property {boolean} available
 * @property {'in_stock' | 'preorder' | 'sold_out'} status
 * @property {string | null} image
 * @property {string | null} imageSrcset
 * @property {string} imageAlt
 * @property {string} swatch
 * @property {boolean} swatchZoom
 */

const MESSAGE_DURATION = 8000;

class AbBundle extends HTMLElement {
  /** @type {Array<{el: HTMLElement, select: HTMLSelectElement, options: BundleOption[], current: BundleOption | null}>} */
  slots = [];
  /** @type {number | undefined} */
  #messageTimer;
  #busy = false;

  connectedCallback() {
    this.slots = Array.from(this.querySelectorAll('[data-ab-bundle-slot]')).map((el) => {
      const select = /** @type {HTMLSelectElement} */ (el.querySelector('[data-ab-bundle-select]'));
      const json = el.querySelector('[data-ab-bundle-options]');
      /** @type {BundleOption[]} */
      let options = [];
      try {
        options = JSON.parse(json?.textContent || '[]');
      } catch (error) {
        console.warn('[ab-bundle] Could not read options', error);
      }
      const current = options.find((o) => String(o.productId) === select?.value) || null;
      return { el: /** @type {HTMLElement} */ (el), select, options, current };
    });

    this.slots.forEach((slot) => {
      slot.select?.addEventListener('change', () => this.#onSelect(slot));
    });

    this.addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ab-bundle-add]'));
    this.addButton?.addEventListener('click', this.#onAdd);

    this.#update();
  }

  disconnectedCallback() {
    this.addButton?.removeEventListener('click', this.#onAdd);
    clearTimeout(this.#messageTimer);
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'ZAR';
    return formatMoney(cents, format, currency);
  }

  /** @param {BundleOption['status']} status */
  #statusLabel(status) {
    if (status === 'sold_out') return this.dataset.labelSoldOut || 'Sold out';
    if (status === 'preorder') return this.dataset.labelPreorder || 'Pre-order';
    return this.dataset.labelInStock || 'In stock';
  }

  /** @param {{el: HTMLElement, select: HTMLSelectElement, options: BundleOption[], current: BundleOption | null}} slot */
  #onSelect(slot) {
    const choice = slot.options.find((o) => String(o.productId) === slot.select.value);
    if (!choice || !choice.available) {
      // Should not happen (sold-out options are disabled); restore the previous choice.
      if (slot.current) slot.select.value = String(slot.current.productId);
      return;
    }
    slot.current = choice;

    const { el } = slot;
    const image = /** @type {HTMLImageElement | null} */ (el.querySelector('[data-ab-bundle-image]'));
    if (image) {
      if (choice.image) {
        if (choice.imageSrcset) image.srcset = choice.imageSrcset;
        image.src = choice.image;
        image.alt = choice.imageAlt || choice.title;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    const swatch = /** @type {HTMLImageElement | null} */ (el.querySelector('[data-ab-bundle-swatch]'));
    if (swatch) {
      if (choice.swatch) {
        swatch.src = choice.swatch;
        swatch.hidden = false;
      } else {
        swatch.hidden = true;
      }
      swatch.parentElement?.classList.toggle('ab-bundle__swatch--zoom', choice.swatchZoom);
    }

    const link = /** @type {HTMLAnchorElement | null} */ (el.querySelector('[data-ab-bundle-link]'));
    if (link) link.href = choice.url;

    const price = el.querySelector('[data-ab-bundle-price]');
    if (price) price.textContent = this.#money(choice.price);

    el.dataset.price = String(choice.price);
    el.dataset.variantId = String(choice.variantId);
    el.dataset.status = choice.status;

    this.#hideMessage();
    this.#update();
  }

  #update() {
    const chosen = this.slots.map((s) => s.current);
    const complete = chosen.length === 3 && chosen.every((c) => c && c.available);

    this.slots.forEach(({ el, current }) => {
      const status = el.querySelector('[data-ab-bundle-status]');
      if (status) status.textContent = this.#statusLabel(current ? current.status : 'sold_out');
    });

    const total = chosen.reduce((sum, c) => sum + (c ? Number(c.price) : 0), 0);
    const pct = Number(this.dataset.discount) || 0;
    const saving = Math.round((total * pct) / 100);
    const bundlePrice = total - saving;

    const totalEl = this.querySelector('[data-ab-bundle-total]');
    const nowEl = this.querySelector('[data-ab-bundle-now]');
    const savingEl = this.querySelector('[data-ab-bundle-saving]');
    if (totalEl) totalEl.textContent = this.#money(total);
    if (nowEl) nowEl.textContent = this.#money(bundlePrice);
    if (savingEl) savingEl.textContent = this.#money(saving);

    const timing = this.querySelector('[data-ab-bundle-timing]');
    if (timing) {
      const hasPreorder = chosen.some((c) => c && c.status === 'preorder');
      timing.textContent = (hasPreorder ? this.dataset.timingPreorder : this.dataset.timingStandard) || '';
    }

    if (this.addButton && !this.#busy) this.addButton.disabled = !complete;
  }

  /**
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(text, type) {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-ab-bundle-message]'));
    if (!message) return;
    clearTimeout(this.#messageTimer);
    message.textContent = text;
    message.dataset.type = type;
    message.setAttribute('role', type === 'error' ? 'alert' : 'status');
    message.hidden = false;
    if (type === 'success') {
      this.#messageTimer = window.setTimeout(() => this.#hideMessage(), MESSAGE_DURATION);
    }
  }

  #hideMessage() {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-ab-bundle-message]'));
    if (!message) return;
    message.hidden = true;
    message.textContent = '';
  }

  #bundleId() {
    const random =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().split('-')[0]
        : Math.random().toString(36).slice(2, 10);
    return `AB-${Date.now().toString(36)}-${random}`.toUpperCase();
  }

  #routes() {
    // @ts-ignore Theme is a global set in layout/theme.liquid
    const routes = (window.Theme && window.Theme.routes) || {};
    /** @param {string | undefined} url @param {string} fallback */
    const js = (url, fallback) => {
      const base = url || fallback;
      return base.endsWith('.js') ? base : `${base}.js`;
    };
    return {
      add: js(routes.cart_add_url, '/cart/add'),
      cart: js(routes.cart_url, '/cart'),
      update: js(routes.cart_update_url, '/cart/update'),
    };
  }

  async #fetchCart() {
    const response = await fetch(this.#routes().cart, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Removes any lines of this bundle that made it into the cart before an error,
   * so a failed bundle never leaves a partial set behind.
   * @param {string} bundleId
   */
  async #removePartial(bundleId) {
    try {
      const cart = await this.#fetchCart();
      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of cart.items || []) {
        if (item.properties && item.properties._bundle === bundleId) updates[item.key] = 0;
      }
      if (Object.keys(updates).length === 0) return false;
      await fetch(this.#routes().update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      return true;
    } catch (error) {
      console.warn('[ab-bundle] Could not clean up a partial bundle', error);
      return false;
    }
  }

  #onAdd = async () => {
    if (this.#busy || !this.addButton) return;
    const chosen = this.slots.map((s) => s.current);
    if (chosen.length !== 3 || chosen.some((c) => !c || !c.available)) return;

    this.#busy = true;
    this.#hideMessage();
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    const bundleId = this.#bundleId();
    const bundleName = this.dataset.bundleName || 'Nursery bundle';
    const lines = /** @type {BundleOption[]} */ (chosen);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const payload = {
      items: lines.map((c) => ({
        id: Number(c.variantId),
        quantity: 1,
        properties: { _bundle: bundleId, _bundle_name: bundleName },
      })),
      ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
    };

    let deferred = null;

    try {
      const response = await fetch(this.#routes().add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        // Net cart is unchanged after the clean-up, so the cart UI needs no refresh.
        await this.#removePartial(bundleId);
        this.#showMessage(data.description || data.message || this.dataset.errorText || 'Something went wrong.', 'error');
        return;
      }

      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((c) => ({ merchandiseId: String(c.variantId), quantity: 1 })),
          promise: deferred.promise,
        })
      );

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ab-bundle',
          sourceId: this.dataset.sectionId,
          itemCount: lines.length,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });

      this.#showMessage(this.dataset.addedText || 'Added to your bag.', 'success');
    } catch (error) {
      console.error('[ab-bundle]', error);
      if (deferred) deferred.reject(error);
      this.#showMessage(this.dataset.errorText || 'Something went wrong.', 'error');
    } finally {
      this.#busy = false;
      this.addButton.removeAttribute('aria-busy');
      this.#update();
    }
  };
}

if (!customElements.get('ab-bundle')) customElements.define('ab-bundle', AbBundle);
